import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { useNavigate } from "react-router-dom"

import { useApolloClient, useMutation, useQuery } from "@apollo/client"
import * as Tabs from "@radix-ui/react-tabs"
import classNames from "classnames"
import _ from "lodash"
import { useHotkeys } from "react-hotkeys-hook"

import BundleClaimConfirmModal from "./BundleClaimModal/BundleClaimConfirmModal"
import BundleClaimErrorModal from "./BundleClaimModal/BundleClaimErrorModal"
import { useBundleClaim } from "./BundleClaimModal/useBundleClaim"
import CancelSortControl from "./CancelSortControl/CancelSortControl"
import CancelsForRereservationTableView from "./CancelsTableView/CancelsForRereservationTableView"
import {
	showUpdatePaymentStatusButton,
	updatePaymentStatusButtonTitle,
} from "./CancelsTableView/CancelsTableView.common.logic"
import MobileCancelsForRereservationView from "./CancelsTableView/MobileCancelsForRereservationTableView"
import styles from "./HomePage.module.scss"
import { useLoadCancelsModal } from "./LoadCancelsModal/LoadCancelsModal"
import MobileSummaryItem from "./Summary/MobileSummaryItem"
import {
	invalidCancelTypeToSendInvoiceMessage,
	invalidPhoneNumberMessage,
} from "./home.common.logic"
import { sortCancels, useCancelSort } from "./useCancelSort"
import { useNotificationBulkClaim } from "./useNotificationBulkClaim"

import QuestionExplanationIcon from "@/lib/components/ui/QuetionExplanationIcon/QuestionExplanationIcon"
import { useMe } from "@/lib/graphql/me"
import reloadImage from "@/lib/images/reload.svg"
import { getUrlEventParams, sendEvent } from "@/lib/ts/event"
import "@/lib/styles/index.css"
import { useFeatureFlag } from "@/main/components/common_features/salon/featureFlag"
import CancelsTableView from "@/main/components/features/home/CancelsTableView/CancelsTableView"
import MobileCancelsTableView from "@/main/components/features/home/CancelsTableView/MobileCancelsTableView"
import {
	EditingRereservationRow,
	EditingRow,
	Tab,
	confirmUpdatePaymentStatusButtonMessage,
} from "@/main/components/features/home/HomePage.logic"
import {
	MIN_INVOICE_PRICE,
	convertCancelTypeToCreateCancelCancelTypeInput,
} from "@/main/domain/cancel"
import { isValidCancelTypeToSendInvoice } from "@/main/domain/home"
import { getFragmentData, graphql } from "@/main/gql/__generated__"
import {
	CancelForListFragment,
	CancelPaymentStatus,
	CancelType,
	CouponMasterNameFragmentDoc,
} from "@/main/gql/__generated__/graphql"
import { useMediaQueryContext } from "@/provider/MediaQueryProvider"
import apiService from "@/provider/rest"
import { useCurrentCompanyId } from "@/souhl_admin/lib/useCurrentCompanyId"
import { isValidPhoneNumber } from "@/utils/phoneNumber"
import { toStringWithCommas } from "@/utils/strings/number"

const noopActionButton = () => undefined

const calcSummaryPrice = (
	cancels: readonly CancelForListFragment[],
	paymentStatuses: CancelPaymentStatus[]
) => {
	let totalPrice = 0
	for (const cancel of cancels) {
		if (paymentStatuses.includes(cancel.paymentStatus)) {
			totalPrice += cancel.invoicePrice
		}
	}
	return totalPrice
}

const GetInvoicePrice = graphql(/* GraphQL */ `
	query GetInvoicePrice($param: InvoicePriceParam!) {
		invoicePrice(param: $param) {
			invoicePrice
		}
	}
`)

const GetClaimedCancels = graphql(/* GraphQL */ `
	query GetClaimedCancels {
		claimedCancels {
			cancels {
				...cancelForList
			}
		}
	}
`)

const GetCouponMastersToSelect = graphql(/* GraphQL */ `
	query GetCouponMastersToSelect($param: CouponMastersParam!) {
		couponMasters(param: $param) {
			...couponMasterToSelect
		}
	}
`)

const GetNotClaimedCancels = graphql(/* GraphQL */ `
	query GetNotClaimedCancels {
		notClaimedCancels {
			cancels {
				...cancelForList
			}
		}
	}
`)

const GetPaidCancels = graphql(/* GraphQL */ `
	query GetPaidCancels {
		paidCancels {
			cancels {
				...cancelForList
			}
		}
	}
`)

const GetNotSubjectCancels = graphql(/* GraphQL */ `
	query GetNotSubjectCancels {
		notSubjectCancels {
			cancels {
				...cancelForList
			}
		}
	}
`)

const GetEncourageRereservationMessageNotSentCancels = graphql(/* GraphQL */ `
	query GetEncourageRereservationMessageNotSentCancels {
		encourageRereservationMessageNotSentCancels {
			cancels {
				...cancelForRereservation
			}
		}
	}
`)

const GetEncourageRereservationMessageSentCancels = graphql(/* GraphQL */ `
	query GetEncourageRereservationMessageSentCancels {
		encourageRereservationMessageSentCancels {
			cancels {
				...cancelForRereservation
			}
		}
	}
`)

const GetRereservedCancels = graphql(/* GraphQL */ `
	query GetRereservedCancels {
		rereservedCancels {
			cancels {
				...cancelForRereservation
			}
		}
	}
`)

// login / GetMe の共有 fragment に入れると login (salon_id header 無し) でも resolve されてしまうので、
// salon_id header 付きで来るこの画面専用のクエリで取る
const GetLapsedAfterCancelHighlightEnabled = graphql(/* GraphQL */ `
	query GetLapsedAfterCancelHighlightEnabled {
		me {
			salon {
				id
				isLapsedAfterCancelHighlightEnabled
			}
		}
	}
`)

const cancelForListFragment = graphql(/* GraphQL */ `
	fragment cancelForList on Cancel {
		id
		idv2
		salonId
		reserveId
		cancelType
		paymentStatus
		visitCounts
		isVisitCountInvalid
		reserveRoute
		originPrice
		invoicePrice
		name
		staffName
		paidTime
		phoneNumber
		dateVisit
		dateCancel
		cancelCouponMasterSpecifiedBy
		isCreatedByUi
		shouldUseDefaultCouponMaster
		hasLapsedAfterCancel
		couponMaster {
			...couponMasterName
		}
	}
`)

const cancelForRereservationFragment = graphql(/* GraphQL */ `
	fragment cancelForRereservation on Cancel {
		idv2
		salonId
		reserveId
		cancelType
		messageKind
		rereservationStatus
		visitCounts
		isVisitCountInvalid
		isCreatedByUi
		originPrice
		name
		staffName
		phoneNumber
		rereservedTime
		dateVisit
		dateCancel
		claimingConversionAppliedTime
		rereservation {
			cancelId
			scrapingTargetSiteReserveId
			price
			discountPrice
			startTime
			staffName
			menuNames
			couponNames
			durationMinute
		}
	}
`)

const UpdateCancel = graphql(/* GraphQL */ `
	mutation UpdateCancel($input: UpdateCancelInput!) {
		updateCancel(input: $input) {
			...cancelForList
		}
	}
`)

const UpdateCancelForRereservation = graphql(/* GraphQL */ `
	mutation UpdateCancelForRereservation(
		$input: UpdateCancelForRereservationInput!
	) {
		updateCancelForRereservation(input: $input) {
			...cancelForList
		}
	}
`)

const SendInvoice = graphql(/* GraphQL */ `
	mutation SendInvoice($input: SendInvoiceInput!) {
		sendInvoice(input: $input) {
			idv2
			paymentId
		}
	}
`)

const ScheduleEncourageRereservation = graphql(/* GraphQL */ `
	mutation ScheduleEncourageRereservation(
		$input: ScheduleEncourageRereservationInput!
	) {
		scheduleEncourageRereservation(input: $input) {
			cancel {
				idv2
			}
		}
	}
`)

const AbortClaim = graphql(/* GraphQL */ `
	mutation AbortClaim($input: AbortClaimInput!) {
		abortClaim(input: $input) {
			idv2
		}
	}
`)

const BulkUpdateCancelPaymentStatus = graphql(/* GraphQL */ `
	mutation BulkUpdateCancelPaymentStatus(
		$input: BulkUpdateCancelPaymentStatusInput!
	) {
		bulkUpdateCancelPaymentStatus(input: $input) {
			id
			salonId
			reserveId
			cancelType
			paymentStatus
			visitCounts
			reserveRoute
			originPrice
			invoicePrice
			name
			phoneNumber
			dateVisit
			dateCancel
			isCouponEnabled
		}
	}
`)

const HomePage = () => {
	document.title = "RefundHub | ホーム"
	const apolloClient = useApolloClient()
	const navigate = useNavigate()

	const { isMobile, isIPad } = useMediaQueryContext()
	const tabsRef = useRef<HTMLDivElement>(null)

	// URLパラメータからイベント用パラメータを取得（マウント時に1回だけ）
	const [eventParams] = useState(() =>
		getUrlEventParams([
			"notification_log_id",
			"without_bundle_claim_notification_log_id",
		])
	)

	const [editingRowMap, setEditingRowMap] = useState<
		Record<string, EditingRow>
	>({}) // key is row idv2

	const [editingRereservationRowMap, setEditingRereservationRowMap] = useState<
		Record<string, EditingRereservationRow>
	>({}) // key is row idv2

	const [checkedNotSentInvoiceCancelMap, setCheckedNotSentInvoiceCancelMap] =
		useState<Record<number, CancelForListFragment>>({}) // key is cancel id
	const [checkedNotSubjectCancelIdMap, setCheckedNotSubjectCancelIdMap] =
		useState<Record<number, CancelForListFragment>>({}) // key is cancel id

	const defaultTab = "INVOICE_NOT_SENT"
	const [currentTab, setCurrentTab] = useState<Tab>(defaultTab)
	const { sort, sortableKeys, toggleSort } = useCancelSort(currentTab)
	const currentTabCheckedCancelIdMap = () => {
		switch (currentTab) {
			case "INVOICE_NOT_SENT":
				return checkedNotSentInvoiceCancelMap
			case "NOT_SUBJECT":
				return checkedNotSubjectCancelIdMap
			default:
				return {}
		}
	}

	const { salon, salonUser, salonDetail, refetch: refetchMe } = useMe()
	const selectableCancelTypes =
		salon?.selectableCancelTypesWithoutUnspecifiedOnes ?? []
	const canSync = salon?.canEnqueueSyncCancels ?? false
	const isReservationScrapingActive =
		salonDetail?.isReservationScrapingActive ?? false
	const enableDefaultCouponMasterForNoConfirmationCancel =
		salon?.enableDefaultCouponMasterForNoConfirmationCancel ?? false
	const defaultCouponMaster = salon?.defaultCouponMaster ?? undefined
	const copySalonIdToClipboard = useCallback(
		(event: KeyboardEvent) => {
			if (!salon) {
				return
			}
			navigator.clipboard.writeText(salon.id.toString())
		},
		[salon]
	)
	useHotkeys("ctrl+shift+c", copySalonIdToClipboard)

	const { companyId: currentCompanyId } = useCurrentCompanyId()
	const navigateToCompanyAdmin = useCallback(() => {
		if (currentCompanyId) {
			navigate(
				`/souhl_admin/companies/${currentCompanyId}?tab=sending-rule-master`
			)
		} else {
			navigate("/souhl_admin")
		}
	}, [navigate, currentCompanyId])
	useHotkeys("ctrl+shift+a", navigateToCompanyAdmin)

	const {
		data: notClaimedCancelsData,
		refetch: refetchNotClaimedCancels,
		loading: notClaimedCancelsLoading,
	} = useQuery(GetNotClaimedCancels, {
		fetchPolicy: "no-cache",
	})

	const { data: claimedCancelsData, refetch: refetchClaimedCancelsData } =
		useQuery(GetClaimedCancels, {
			fetchPolicy: "no-cache",
		})

	const invoiceNotSentCancels = useMemo(() => {
		// fragmentをuseEffectのdependencyにする場合、useMemoが必要
		return sortCancels(
			getFragmentData(
				cancelForListFragment,
				notClaimedCancelsData?.notClaimedCancels.cancels
			) ?? [],
			sort
		)
	}, [notClaimedCancelsData, sort])
	const invoiceNotSentSummaryPrice = calcSummaryPrice(invoiceNotSentCancels, [
		"INVOICE_NOT_SENT",
		"SCHEDULED_TO_CLAIM",
	])

	const invoiceSentCancels = useMemo(() => {
		// fragmentをuseEffectのdependencyにする場合、useMemoが必要
		return sortCancels(
			getFragmentData(
				cancelForListFragment,
				claimedCancelsData?.claimedCancels.cancels
			) ?? [],
			sort
		)
	}, [claimedCancelsData, sort])
	const claimedCancelsCount = invoiceSentCancels.filter(
		(c) => c.paymentStatus === "INVOICE_SENT"
	).length
	const invoiceSentSummaryPrice = calcSummaryPrice(invoiceSentCancels, [
		"INVOICE_SENT",
	])

	const { data: paidCancelsData, refetch: refetchPaidCancelsData } = useQuery(
		GetPaidCancels,
		{
			fetchPolicy: "no-cache",
		}
	)
	const paidCancels = useMemo(() => {
		// fragmentをuseEffectのdependencyにする場合、useMemoが必要
		return sortCancels(
			getFragmentData(
				cancelForListFragment,
				paidCancelsData?.paidCancels.cancels
			) ?? [],
			sort
		)
	}, [paidCancelsData, sort])
	const paidSummaryPrice = calcSummaryPrice(paidCancels, ["PAID"])

	const { data: notSubjectCancelsData, refetch: refetchNotSubjectCancelsData } =
		useQuery(GetNotSubjectCancels, {
			fetchPolicy: "no-cache",
		})
	const notSubjectCancels = useMemo(() => {
		return sortCancels(
			getFragmentData(
				cancelForListFragment,
				notSubjectCancelsData?.notSubjectCancels.cancels
			) ?? [],
			sort
		)
	}, [notSubjectCancelsData, sort])

	const hasAnyLapsedCancelInNotClaimedTab = useMemo(() => {
		return invoiceNotSentCancels.some((c) => c.hasLapsedAfterCancel)
	}, [invoiceNotSentCancels])

	// 再予約転換 unit を持つ店舗 (再予約機能 on) は BE が false を返すのでハイライトしない
	const { data: lapsedHighlightData } = useQuery(
		GetLapsedAfterCancelHighlightEnabled,
		{ fetchPolicy: "cache-first" }
	)
	const isLapsedAfterCancelHighlightEnabled =
		lapsedHighlightData?.me.salon.isLapsedAfterCancelHighlightEnabled ?? false
	const enableLapsedHighlight =
		isReservationScrapingActive &&
		isLapsedAfterCancelHighlightEnabled &&
		hasAnyLapsedCancelInNotClaimedTab
	const showLapsedAfterCancelLegend =
		enableLapsedHighlight && currentTab === "INVOICE_NOT_SENT"
	// 離脱判定の日数は feature flag LAPSED_AFTER_CANCEL_3_DAYS_AFTER で 5 日 / 3 日が切り替わるので BE の値を使う
	const lapsedAfterCancelMinDays = salon?.lapsedAfterCancelMinDays ?? 5

	const { featureFlag } = useFeatureFlag()
	const is_encouraging_rereservation_enabled = useMemo(() => {
		return featureFlag?.isEncouragingRereservationEnabled ?? false
	}, [featureFlag])

	const {
		data: encourageRereservationMessageNotSentCancelsData,
		refetch: refetchEncourageRereservationMessageNotSentCancelsData,
	} = useQuery(GetEncourageRereservationMessageNotSentCancels, {
		fetchPolicy: "no-cache",
	})
	const encourageRereservationMessageNotSentCancels =
		getFragmentData(
			cancelForRereservationFragment,
			encourageRereservationMessageNotSentCancelsData
				?.encourageRereservationMessageNotSentCancels.cancels
		) ?? []

	const {
		data: encourageRereservationMessageSentCancelsData,
		refetch: refetchEncourageRereservationotScheduledCancelsData,
	} = useQuery(GetEncourageRereservationMessageSentCancels, {
		fetchPolicy: "no-cache",
	})
	const encourageRereservationMessageSentCancels =
		getFragmentData(
			cancelForRereservationFragment,
			encourageRereservationMessageSentCancelsData
				?.encourageRereservationMessageSentCancels.cancels
		) ?? []

	const { data: rereservedCancelsData, refetch: refetchRereservedCancelsData } =
		useQuery(GetRereservedCancels, {
			fetchPolicy: "no-cache",
		})
	const rereservedCancels =
		getFragmentData(
			cancelForRereservationFragment,
			rereservedCancelsData?.rereservedCancels.cancels
		) ?? []

	const onSuccessLoadCancels = useCallback(() => {
		refetchNotClaimedCancels()
		refetchClaimedCancelsData()
		refetchNotSubjectCancelsData()
		refetchPaidCancelsData()
		refetchEncourageRereservationMessageNotSentCancelsData()
		refetchEncourageRereservationotScheduledCancelsData()
		refetchRereservedCancelsData()
		refetchMe()
	}, [
		refetchNotClaimedCancels,
		refetchClaimedCancelsData,
		refetchNotSubjectCancelsData,
		refetchPaidCancelsData,
		refetchEncourageRereservationMessageNotSentCancelsData,
		refetchEncourageRereservationotScheduledCancelsData,
		refetchRereservedCancelsData,
		refetchMe,
	])
	const { renderModal, startLoadCancels } =
		useLoadCancelsModal(onSuccessLoadCancels)

	const { data: availableCouponMasterData } = useQuery(
		GetCouponMastersToSelect,
		{
			variables: { param: { isActive: true } },
			fetchPolicy: "no-cache",
		}
	)
	const couponMasters = availableCouponMasterData?.couponMasters

	const [mutateBulkUpdateCancelPaymentStatus] = useMutation(
		BulkUpdateCancelPaymentStatus
	)
	const [mutateUpdateCancel] = useMutation(UpdateCancel)
	const [mutateUpdateCancelForRereservation] = useMutation(
		UpdateCancelForRereservation
	)
	const [mutateSendInvoice] = useMutation(SendInvoice)
	const [mutateAbortClaim] = useMutation(AbortClaim)
	const [mutateScheduleEncourageRereservation] = useMutation(
		ScheduleEncourageRereservation
	)

	const {
		selectedCancelsForBundleClaim,
		isBundleClaimConfirmOpen,
		isBundleClaimSubmitting,
		isBundleClaimErrorOpen,
		bundleClaimFailedCancels,
		onClickBundleClaimButton,
		onConfirmBundleClaim,
		closeBundleClaimConfirm,
		closeBundleClaimError,
	} = useBundleClaim(checkedNotSentInvoiceCancelMap, (cancelIds) => {
		refetchNotClaimedCancels()
		refetchClaimedCancelsData()
		setCheckedNotSentInvoiceCancelMap({})

		// fe-event: bundle_claim
		if (salonUser && salon) {
			sendEvent(salonUser.id, "bundle_claim", {
				salon_id: salon.id,
				cancel_ids: cancelIds,
				has_lapsed_after_cancel_cancel_ids: Object.values(
					checkedNotSentInvoiceCancelMap
				)
					.filter((cancel) => cancel.hasLapsedAfterCancel)
					.map((cancel) => cancel.idv2),
				...eventParams,
			})
		}
	})

	// notification_log_id パラメータからの一括請求モーダル自動表示
	useNotificationBulkClaim({
		invoiceNotSentCancels,
		isInvoiceNotSentCancelsLoaded: !notClaimedCancelsLoading,
		setCheckedNotSentInvoiceCancelMap,
		onClickBundleClaimButton,
	})

	const handleCloseBundleClaimConfirm = useCallback(() => {
		closeBundleClaimConfirm()
	}, [closeBundleClaimConfirm])

	const handleCloseBundleClaimError = useCallback(() => {
		closeBundleClaimError()
	}, [closeBundleClaimError])

	const onTabValueChanged = (value: string) => {
		// NOTE: 請求送信時にcache更新の方が正しいが、めんどいので一旦refetchで更新
		switch (value) {
			case "INVOICE_NOT_SENT":
				setCurrentTab("INVOICE_NOT_SENT")
				refetchNotClaimedCancels()
				break
			case "INVOICE_SENT":
				setCurrentTab("INVOICE_SENT")
				refetchClaimedCancelsData()
				break
			case "PAID":
				setCurrentTab("PAID")
				refetchPaidCancelsData()
				break
			case "NOT_SUBJECT":
				setCurrentTab("NOT_SUBJECT")
				refetchNotSubjectCancelsData()
				break
			case "ENCOURAGING_RERESERVATION_NOT_SCHEDULED":
				setCurrentTab("ENCOURAGING_RERESERVATION_NOT_SCHEDULED")
				refetchEncourageRereservationMessageNotSentCancelsData()
				break
			case "ENCOURAGING_RERESERVATION_SCHEDULED":
				setCurrentTab("ENCOURAGING_RERESERVATION_SCHEDULED")
				refetchEncourageRereservationotScheduledCancelsData()
				break
			case "RERESERVED":
				setCurrentTab("RERESERVED")
				refetchRereservedCancelsData()
				break
			default: // unexpected
		}
	}

	const updateRow = (idv2: string, row: EditingRow) => {
		const newMap = { ...editingRowMap }
		newMap[idv2] = row
		setEditingRowMap(newMap)
	}
	const updateEditingRereservationRow = (
		idv2: string,
		row: EditingRereservationRow
	) => {
		const newMap = { ...editingRereservationRowMap }
		newMap[idv2] = row
		setEditingRereservationRowMap(newMap)
	}

	const onClickRowEdit = async (idv2: string) => {
		const cancelRow = invoiceNotSentCancels.find((row) => row.idv2 === idv2)
		if (!cancelRow) {
			console.log("unexpectedly cancelRow is undefined. idv2=", idv2)
			return
		}

		let couponMasterId: string | undefined = getFragmentData(
			CouponMasterNameFragmentDoc,
			cancelRow.couponMaster
		)?.id
		if (cancelRow.shouldUseDefaultCouponMaster) {
			couponMasterId = defaultCouponMaster?.id
		}

		let cancelType = cancelRow.cancelType
		if (!selectableCancelTypes.includes(cancelType)) {
			cancelType = selectableCancelTypes[0]
		}

		const invoicePrice = await onCalcInvoicePrice(
			cancelRow.visitCounts,
			cancelType,
			cancelRow.originPrice.toString()
		)

		const row: EditingRow = {
			cancelType,
			originPrice: cancelRow.originPrice.toString(),
			invoicePrice,
			visitCounts: cancelRow.visitCounts,
			phoneNumber: cancelRow.phoneNumber,
			name: cancelRow.name,
			couponMasterId,
		}
		updateRow(idv2, row)
	}
	const onClickRereservationRowEdit = (idv2: string) => {
		const cancelRow = encourageRereservationMessageNotSentCancels.find(
			(row) => row.idv2 === idv2
		)
		if (!cancelRow) {
			console.log("unexpectedly cancelRow is undefined. idv2=", idv2)
			return
		}

		const row: EditingRereservationRow = {
			phoneNumber: cancelRow.phoneNumber,
		}
		updateEditingRereservationRow(idv2, row)
	}

	const cancelRowEdit = (idv2: string) => {
		const newMap = { ...editingRowMap }
		delete newMap[idv2]
		setEditingRowMap(newMap)
	}
	const cancelRereservationRowEdit = (idv2: string) => {
		const newMap = { ...editingRereservationRowMap }
		delete newMap[idv2]
		setEditingRereservationRowMap(newMap)
	}

	const onCalcInvoicePrice = async (
		visitCounts: number,
		newCancelType: CancelType,
		newOriginPriceStr: string
	): Promise<number> => {
		if (newCancelType === "UNSPECIFIED") {
			// NOTE: convertCancelTypeToCreateCancelCancelTypeInput throw a error
			// if UNSPECIFIED passed
			return 0
		}

		const invoicePriceRes = await apolloClient.query({
			query: GetInvoicePrice,
			variables: {
				param: {
					originPrice: Number(newOriginPriceStr ?? 0),
					cancelType:
						convertCancelTypeToCreateCancelCancelTypeInput(newCancelType),
					// 既存客の当日キャンセル 50% 請求の判定に使う（実請求と同じ額を表示するため）
					visitCounts,
				},
			},
		})
		return invoicePriceRes.data.invoicePrice.invoicePrice
	}

	const onEditCancelType = async (idv2: string, newCancelType: CancelType) => {
		const row = editingRowMap[idv2]
		const invoicePrice = await onCalcInvoicePrice(
			row.visitCounts,
			newCancelType,
			row.originPrice
		)
		const newEditingRow: EditingRow = {
			...row,
			cancelType: newCancelType,
			invoicePrice,
		}
		updateRow(idv2, newEditingRow)
	}

	const onEditOriginPrice = async (idv2: string, newOriginPriceStr: string) => {
		const row = editingRowMap[idv2]
		const invoicePrice = await onCalcInvoicePrice(
			row.visitCounts,
			row.cancelType,
			newOriginPriceStr
		)

		const newEditingRow: EditingRow = {
			...row,
			originPrice: newOriginPriceStr,
			invoicePrice,
		}
		updateRow(idv2, newEditingRow)
	}

	const onClickUpdateButton = async (idv2: string) => {
		const editingRow = editingRowMap[idv2]
		if (!isValidPhoneNumber(editingRow.phoneNumber)) {
			alert("不正な形式の電話番号です。")
			return
		}

		await mutateUpdateCancel({
			variables: {
				input: {
					id: idv2,
					phoneNumber: editingRow.phoneNumber,
					originPrice: Number(editingRow.originPrice),
					cancelType: editingRow.cancelType,
					name: editingRow.name,
					couponMasterId: editingRow.couponMasterId,
				},
			},
		}).then(() => {
			cancelRowEdit(idv2)
			refetchNotClaimedCancels()
			alert("キャンセル情報を更新しました")
		})
	}
	const onClickUpdateButtonForRereservation = async (idv2: string) => {
		const editingRow = editingRereservationRowMap[idv2]
		if (!isValidPhoneNumber(editingRow.phoneNumber)) {
			alert("不正な形式の電話番号です。")
			return
		}

		await mutateUpdateCancelForRereservation({
			variables: {
				input: {
					id: idv2,
					phoneNumber: editingRow.phoneNumber,
				},
			},
		}).then(() => {
			cancelRereservationRowEdit(idv2)
			refetchEncourageRereservationMessageNotSentCancelsData()
			alert("キャンセル情報を更新しました")
		})
	}
	const onClickSendInvoice = async (idv2: string) => {
		const cancelRow = invoiceNotSentCancels.find((row) => row.idv2 === idv2)
		if (!cancelRow) {
			// TODO: これで未請求タブでしか何も起こらないようになってるが、制御方法雑なのでなんとかしたい
			console.log("unexpectedly cancelRow is undefined. idv2=", idv2)
			return
		}

		let errorMessage = ""
		if (!isValidPhoneNumber(cancelRow.phoneNumber)) {
			errorMessage = invalidPhoneNumberMessage
		}
		if (!isValidCancelTypeToSendInvoice(cancelRow.cancelType)) {
			if (errorMessage) {
				errorMessage += "\n" + invalidCancelTypeToSendInvoiceMessage
			} else {
				errorMessage = invalidCancelTypeToSendInvoiceMessage
			}
		}
		if (errorMessage) {
			alert(errorMessage)
			return
		}

		// 50円未満の決済はstripeでできない
		// https://docs.stripe.com/currencies#minimum-and-maximum-charge-amounts
		if (cancelRow.invoicePrice < MIN_INVOICE_PRICE) {
			alert("金額が小さいため、請求できません。予約金額を確認してください。")
			return
		}
		if (cancelRow.paymentStatus === "INVOICE_NOT_SENT") {
			if (window.confirm("請求を送信します。よろしいですか？")) {
				await mutateSendInvoice({
					variables: {
						input: {
							cancelId: cancelRow.idv2,
						},
					},
				}).then(() => {
					refetchNotClaimedCancels()
					refetchClaimedCancelsData()
					alert("請求を送信しました。")

					// fe-event: claim
					if (salonUser && salon) {
						sendEvent(salonUser.id, "claim", {
							salon_id: salon.id,
							cancel_id: cancelRow.idv2,
							has_lapsed_after_cancel: cancelRow.hasLapsedAfterCancel,
							...eventParams,
						})
					}
				})
			}
		} else if (cancelRow.paymentStatus === "SCHEDULED_TO_CLAIM") {
			if (
				window.confirm(
					"請求を取り消します。よろしいですか？\n(非請求タブに移動します)"
				)
			) {
				await mutateBulkUpdateCancelPaymentStatus({
					variables: {
						input: {
							paymentStatus: "NOT_SUBJECT",
							cancelIds: [cancelRow.id],
						},
					},
				}).then(() => {
					refetchNotClaimedCancels()
					refetchNotSubjectCancelsData()
					alert("請求を取り消しました。")
				})
			}
		}
	}
	const onClickScheduleEncourageRereservation = async (idv2: string) => {
		const cancelRow = [
			...encourageRereservationMessageNotSentCancels,
			...encourageRereservationMessageSentCancels,
		].find((row) => row.idv2 === idv2)
		if (!cancelRow) {
			console.log("unexpectedly cancelRow is undefined. idv2=", idv2)
			return
		}

		const shouldAbort =
			cancelRow.rereservationStatus === "ENCOURAGING_RERESERVATION_SCHEDULED" ||
			cancelRow.rereservationStatus === "ENCOURAGING_MESSAGE_SENT"

		if (shouldAbort) {
			if (window.confirm("再予約案内を送付停止します。よろしいですか？")) {
				apiService
					.post(
						`/public/abort_encouraging_rereservation_message/cancel/${cancelRow.idv2}`,
						{}
					)
					.then(() => {
						refetchEncourageRereservationMessageNotSentCancelsData()
						refetchEncourageRereservationotScheduledCancelsData()
						alert("再予約案内を停止しました。")
					})
					.catch((error) => {
						console.error(error)
						alert("エラーが発生しました。時間が経ってから再度お試しください。")
					})
			}
		} else {
			if (window.confirm("再予約案内を送付します。よろしいですか？")) {
				await mutateScheduleEncourageRereservation({
					variables: {
						input: {
							cancelId: cancelRow.idv2,
						},
					},
				}).then(() => {
					refetchEncourageRereservationMessageNotSentCancelsData()
					refetchEncourageRereservationotScheduledCancelsData()
					alert("完了しました。")
				})
			}
		}
	}

	// 請求済みタブのみ
	const onClickAbortClaim = async (idv2: string) => {
		const cancelRow = invoiceSentCancels.find((row) => row.idv2 === idv2)
		if (!cancelRow) {
			// ここにはこないはず
			return
		}

		if (cancelRow.paymentStatus === "CLAIM_ABORTED") {
			// すでに取消済
			return
		}

		if (window.confirm("請求を取り消します。よろしいですか？")) {
			await mutateAbortClaim({
				variables: {
					input: {
						cancelId: cancelRow.idv2,
					},
				},
			}).then(() => {
				refetchNotClaimedCancels()
				refetchClaimedCancelsData()
				alert("請求を取り消しました。")
			})
		}
	}

	const onClickCheckbox = (
		cancel: CancelForListFragment,
		currentTab: "INVOICE_NOT_SENT" | "NOT_SUBJECT"
	) => {
		const prevCheckedMap =
			currentTab === "INVOICE_NOT_SENT"
				? checkedNotSentInvoiceCancelMap
				: checkedNotSubjectCancelIdMap
		const setCheckedMap =
			currentTab === "INVOICE_NOT_SENT"
				? setCheckedNotSentInvoiceCancelMap
				: setCheckedNotSubjectCancelIdMap

		const newCheckedMap = { ...prevCheckedMap }
		if (cancel.id in newCheckedMap) {
			delete newCheckedMap[cancel.id]
		} else {
			newCheckedMap[cancel.id] = cancel
		}
		setCheckedMap(newCheckedMap)
	}

	// 全件選択チェックボックスの処理（CancelsTableView用）
	const onSelectAllCheckbox = (isSelected: boolean) => {
		const cancels =
			currentTab === "INVOICE_NOT_SENT"
				? invoiceNotSentCancels
				: currentTab === "NOT_SUBJECT"
					? notSubjectCancels
					: []

		if (cancels.length === 0) return

		const setCheckedMap =
			currentTab === "INVOICE_NOT_SENT"
				? setCheckedNotSentInvoiceCancelMap
				: setCheckedNotSubjectCancelIdMap

		setCheckedMap(
			isSelected
				? Object.fromEntries(cancels.map((cancel) => [cancel.id, cancel]))
				: {}
		)
	}

	const onClickUpdatePaymentStatusButton = async () => {
		let cancelIds: number[] = []
		let paymentStatus
		let confirmMessage = ""
		if (currentTab === "INVOICE_NOT_SENT" && notClaimedCancelsData) {
			paymentStatus = "NOT_SUBJECT"
			cancelIds = Object.keys(checkedNotSentInvoiceCancelMap).map((cancelId) =>
				parseInt(cancelId, 10)
			)
			confirmMessage = confirmUpdatePaymentStatusButtonMessage(
				invoiceNotSentCancels,
				checkedNotSentInvoiceCancelMap,
				currentTab
			)
		} else if (currentTab === "NOT_SUBJECT" && notSubjectCancelsData) {
			paymentStatus = "INVOICE_NOT_SENT"
			cancelIds = Object.keys(checkedNotSubjectCancelIdMap).map((cancelId) =>
				parseInt(cancelId, 10)
			)
			confirmMessage = confirmUpdatePaymentStatusButtonMessage(
				notSubjectCancels,
				checkedNotSubjectCancelIdMap,
				currentTab
			)
		} else {
			return // unexpected
		}

		if (window.confirm(confirmMessage)) {
			await mutateBulkUpdateCancelPaymentStatus({
				variables: {
					input: {
						paymentStatus,
						cancelIds,
					},
				},
			})

			refetchNotClaimedCancels()
			refetchNotSubjectCancelsData()
			alert("キャンセル情報を更新しました")

			// fe-event
			if (salonUser && salon) {
				const eventName =
					currentTab === "INVOICE_NOT_SENT"
						? "move_to_not_subject"
						: "move_to_invoice_not_sent"
				const cancels = Object.values(
					currentTab === "INVOICE_NOT_SENT"
						? checkedNotSentInvoiceCancelMap
						: checkedNotSubjectCancelIdMap
				)
				const cancelIdv2s = cancels.map((cancel) => cancel.idv2)
				sendEvent(salonUser.id, eventName, {
					salon_id: salon.id,
					cancel_ids: cancelIdv2s,
					has_lapsed_after_cancel_cancel_ids: cancels
						.filter((cancel) => cancel.hasLapsedAfterCancel)
						.map((cancel) => cancel.idv2),
					...eventParams,
				})
			}
		}
	}
	const onClickScrollUpTriangle = () => {
		if (tabsRef.current) {
			tabsRef.current.scrollIntoView({
				behavior: "smooth",
				// block: "center",
			})
		}
	}

	useEffect(() => {
		// 未請求に無いものは選択されたsetから消し、残るものは最新データに更新する
		const newMap: Record<number, CancelForListFragment> = {}
		Object.keys(checkedNotSentInvoiceCancelMap).forEach((cancelId) => {
			const latestCancel = invoiceNotSentCancels.find(
				(cancel) => cancel.id.toString() === cancelId
			)
			if (latestCancel) {
				newMap[Number(cancelId)] = latestCancel
			}
		})
		if (!_.isEqual(newMap, checkedNotSentInvoiceCancelMap)) {
			setCheckedNotSentInvoiceCancelMap(newMap)
		}
	}, [invoiceNotSentCancels])
	useEffect(() => {
		// 非請求に無いものは選択されたsetから消す
		// 将来的にpaginationを追加したらこの処理は必要だが、今はなくてもいい
		const newMap = {}
		Object.entries(checkedNotSubjectCancelIdMap).forEach(([cancelId, _]) => {
			if (
				notSubjectCancels.find((cancel) => cancel.id.toString() === cancelId)
			) {
				newMap[cancelId] = checkedNotSubjectCancelIdMap[cancelId]
			}
		})
		if (_.size(newMap) !== _.size(checkedNotSubjectCancelIdMap)) {
			setCheckedNotSubjectCancelIdMap(newMap)
		}
	}, [notSubjectCancels, checkedNotSubjectCancelIdMap])

	return (
		<div className={styles.home}>
			{renderModal()}
			<BundleClaimConfirmModal
				isOpen={isBundleClaimConfirmOpen}
				onClose={handleCloseBundleClaimConfirm}
				selectedCancels={selectedCancelsForBundleClaim}
				isSubmitting={isBundleClaimSubmitting}
				onConfirm={onConfirmBundleClaim}
			/>
			<BundleClaimErrorModal
				isOpen={isBundleClaimErrorOpen}
				onClose={handleCloseBundleClaimError}
				failedCancels={bundleClaimFailedCancels}
			/>
			<div className={styles.main_top_content}>
				<h1 className={styles.title}>
					{canSync && (
						<img
							src={reloadImage}
							alt="sync"
							className={styles.reload_button}
							onClick={startLoadCancels}
						/>
					)}
					&ensp;キャンセル情報一覧
				</h1>
				{/* enableする予定
        {!isMobile && (
          <button
            className={styles.location_reload_button}
            onClick={() => {
              window.location.reload();
            }}
          >
            リロード
          </button>
        )} */}
			</div>

			{isMobile ? (
				<div className={styles.mobile_summary}>
					<MobileSummaryItem
						title="未請求"
						amount={invoiceNotSentSummaryPrice}
					/>
					<MobileSummaryItem title="請求済" amount={invoiceSentSummaryPrice} />
					<MobileSummaryItem title="回収済" amount={paidSummaryPrice} />
				</div>
			) : (
				<div className={styles.summary}>
					<div className={styles.summary__item}>
						<p className={styles.summary__item_title}>未請求</p>
						<div className={styles.summary__item_value}>
							<p className={styles.summary__item_value_amount}>
								¥
								<span className={styles.amount_num}>
									{toStringWithCommas(invoiceNotSentSummaryPrice)}
								</span>
							</p>
							<p className={styles.summary__item_value_count}>
								{invoiceNotSentCancels.length}件
							</p>
						</div>
					</div>
					<div className={styles.summary__item}>
						<p className={styles.summary__item_title}>請求済</p>
						<div className={styles.summary__item_value}>
							<p className={styles.summary__item_value_amount}>
								¥
								<span className={styles.amount_num}>
									{toStringWithCommas(invoiceSentSummaryPrice)}
								</span>
							</p>
							<p className={styles.summary__item_value_count}>
								{claimedCancelsCount}件
							</p>
						</div>
					</div>
					<div className={styles.summary__item}>
						<p className={styles.summary__item_title}>回収済</p>
						<div className={styles.summary__item_value}>
							<p className={styles.summary__item_value_amount}>
								¥
								<span className={styles.amount_num}>
									{toStringWithCommas(paidSummaryPrice)}
								</span>
							</p>
							<p className={styles.summary__item_value_count}>
								{paidCancels.length}件
							</p>
						</div>
					</div>
				</div>
			)}

			<div className={styles.main_bottom_content}>
				<Tabs.Root defaultValue={defaultTab} onValueChange={onTabValueChanged}>
					<Tabs.List ref={tabsRef} className={styles.tabs_container}>
						<Tabs.Trigger value="INVOICE_NOT_SENT" className={styles.tab}>
							未請求
						</Tabs.Trigger>
						<Tabs.Trigger value="INVOICE_SENT" className={styles.tab}>
							請求済
						</Tabs.Trigger>
						<Tabs.Trigger value="PAID" className={styles.tab}>
							回収済
						</Tabs.Trigger>
						<Tabs.Trigger value="NOT_SUBJECT" className={styles.tab}>
							非請求
						</Tabs.Trigger>
						{is_encouraging_rereservation_enabled && (
							<div className={styles.encouraging_rereservation_tabs_container}>
								<Tabs.Trigger
									value="ENCOURAGING_RERESERVATION_NOT_SCHEDULED"
									className={classNames(
										styles.tab,
										styles.encouraging_rereservation_not_scheduled_tab
									)}
								>
									再予約案内未送付
								</Tabs.Trigger>
								<Tabs.Trigger
									value="ENCOURAGING_RERESERVATION_SCHEDULED"
									className={styles.tab}
								>
									送付済
								</Tabs.Trigger>
								<Tabs.Trigger value="RERESERVED" className={styles.tab}>
									再予約済
								</Tabs.Trigger>
							</div>
						)}
						{!isMobile &&
							showUpdatePaymentStatusButton(
								currentTab,
								currentTabCheckedCancelIdMap()
							) && (
								<div
									className={
										isIPad
											? styles.update_payment_status_container_ipad
											: styles.update_payment_status_container
									}
								>
									<span className={styles.selected_count}>
										{_.size(currentTabCheckedCancelIdMap())}件選択
									</span>
									<button
										onClick={async () =>
											await onClickUpdatePaymentStatusButton()
										}
									>
										{updatePaymentStatusButtonTitle(currentTab)}
									</button>
									{currentTab === "INVOICE_NOT_SENT" && (
										<button
											className={styles.bundle_claim_button}
											onClick={onClickBundleClaimButton}
										>
											請求する
										</button>
									)}
								</div>
							)}
					</Tabs.List>
					{showLapsedAfterCancelLegend && (
						<div className={styles.legend_area}>
							<div className={styles.legend_swatch} />
							<span className={styles.legend_text}>請求を推奨する離脱顧客</span>
							<QuestionExplanationIcon
								contentMessage={`※ キャンセル後 ${lapsedAfterCancelMinDays} 日以上経過しても再予約をしていないお客様で、再来余地が低いお客様です。`}
							/>
						</div>
					)}
					{/* PC はテーブルヘッダーをクリックして並び替えるので、
					    ヘッダーを持たないスマホ表示でだけボタンを出す */}
					{isMobile && (
						<CancelSortControl
							sortKeys={sortableKeys}
							sort={sort}
							onClickSortKey={toggleSort}
						/>
					)}
					{isMobile ? (
						<div>
							<Tabs.Content value="INVOICE_NOT_SENT">
								<MobileCancelsTableView
									currentTab={currentTab}
									cancels={invoiceNotSentCancels}
									enableLapsedHighlight={enableLapsedHighlight}
									checkedCancelMap={checkedNotSentInvoiceCancelMap}
									availableCouponMasters={couponMasters ?? []}
									selectableCancelTypes={selectableCancelTypes}
									editingRowMap={editingRowMap}
									enableDefaultCouponMasterForNoConfirmationCancel={
										enableDefaultCouponMasterForNoConfirmationCancel
									}
									defaultCouponMaster={defaultCouponMaster}
									updateRow={updateRow}
									onEditCancelType={onEditCancelType}
									onEditOriginPrice={onEditOriginPrice}
									onClickUpdateButton={onClickUpdateButton}
									onClickActionButton={onClickSendInvoice}
									cancelRowEdit={cancelRowEdit}
									onClickRowEdit={onClickRowEdit}
									onClickCheckbox={(cancel) =>
										onClickCheckbox(cancel, "INVOICE_NOT_SENT")
									}
									onClickUpdatePaymentStatusButton={
										onClickUpdatePaymentStatusButton
									}
									onClickBundleClaimButton={onClickBundleClaimButton}
									onClickScrollUpTriangle={onClickScrollUpTriangle}
									onSelectAllCheckbox={onSelectAllCheckbox}
								/>
							</Tabs.Content>
							<Tabs.Content value="INVOICE_SENT">
								<MobileCancelsTableView
									currentTab={currentTab}
									cancels={invoiceSentCancels}
									enableLapsedHighlight={false}
									editingRowMap={editingRowMap}
									enableDefaultCouponMasterForNoConfirmationCancel={
										enableDefaultCouponMasterForNoConfirmationCancel
									}
									availableCouponMasters={couponMasters ?? []}
									selectableCancelTypes={selectableCancelTypes}
									defaultCouponMaster={defaultCouponMaster}
									updateRow={updateRow}
									onEditCancelType={onEditCancelType}
									onEditOriginPrice={onEditOriginPrice}
									onClickUpdateButton={onClickUpdateButton}
									onClickActionButton={onClickAbortClaim}
									cancelRowEdit={cancelRowEdit}
									onClickRowEdit={onClickRowEdit}
								/>
							</Tabs.Content>
							<Tabs.Content value="PAID">
								<MobileCancelsTableView
									currentTab={currentTab}
									cancels={paidCancels}
									enableLapsedHighlight={false}
									editingRowMap={editingRowMap}
									enableDefaultCouponMasterForNoConfirmationCancel={
										enableDefaultCouponMasterForNoConfirmationCancel
									}
									availableCouponMasters={couponMasters ?? []}
									selectableCancelTypes={selectableCancelTypes}
									defaultCouponMaster={defaultCouponMaster}
									updateRow={updateRow}
									onEditCancelType={onEditCancelType}
									onEditOriginPrice={onEditOriginPrice}
									onClickUpdateButton={onClickUpdateButton}
									onClickActionButton={onClickSendInvoice}
									cancelRowEdit={cancelRowEdit}
									onClickRowEdit={onClickRowEdit}
								/>
							</Tabs.Content>
							<Tabs.Content value="NOT_SUBJECT">
								<MobileCancelsTableView
									currentTab={currentTab}
									cancels={notSubjectCancels}
									enableLapsedHighlight={false}
									checkedCancelMap={checkedNotSubjectCancelIdMap}
									editingRowMap={editingRowMap}
									enableDefaultCouponMasterForNoConfirmationCancel={
										enableDefaultCouponMasterForNoConfirmationCancel
									}
									availableCouponMasters={couponMasters ?? []}
									selectableCancelTypes={selectableCancelTypes}
									defaultCouponMaster={defaultCouponMaster}
									updateRow={updateRow}
									onEditCancelType={onEditCancelType}
									onEditOriginPrice={onEditOriginPrice}
									onClickUpdateButton={onClickUpdateButton}
									onClickActionButton={onClickSendInvoice}
									cancelRowEdit={cancelRowEdit}
									onClickRowEdit={onClickRowEdit}
									onClickCheckbox={(cancel) =>
										onClickCheckbox(cancel, "NOT_SUBJECT")
									}
									onClickUpdatePaymentStatusButton={
										onClickUpdatePaymentStatusButton
									}
									onClickScrollUpTriangle={onClickScrollUpTriangle}
									onSelectAllCheckbox={onSelectAllCheckbox}
								/>
							</Tabs.Content>
							<Tabs.Content value="ENCOURAGING_RERESERVATION_NOT_SCHEDULED">
								<MobileCancelsForRereservationView
									currentTab="ENCOURAGING_RERESERVATION_NOT_SCHEDULED"
									cancels={encourageRereservationMessageNotSentCancels}
									editingRereservationRowMap={editingRereservationRowMap}
									updateEditingRereservationRow={updateEditingRereservationRow}
									onClickUpdateButton={onClickUpdateButtonForRereservation}
									onClickActionButton={onClickScheduleEncourageRereservation}
									onClickRereservationRowEdit={onClickRereservationRowEdit}
									cancelRereservationRowEdit={cancelRereservationRowEdit}
								/>
							</Tabs.Content>
							<Tabs.Content value="ENCOURAGING_RERESERVATION_SCHEDULED">
								<MobileCancelsForRereservationView
									currentTab="ENCOURAGING_RERESERVATION_SCHEDULED"
									cancels={encourageRereservationMessageSentCancels}
									editingRereservationRowMap={editingRereservationRowMap}
									updateEditingRereservationRow={updateEditingRereservationRow}
									onClickUpdateButton={onClickUpdateButtonForRereservation}
									onClickActionButton={onClickScheduleEncourageRereservation}
									onClickRereservationRowEdit={onClickRereservationRowEdit}
									cancelRereservationRowEdit={cancelRereservationRowEdit}
								/>
							</Tabs.Content>
							<Tabs.Content value="RERESERVED">
								<MobileCancelsForRereservationView
									currentTab="RERESERVED"
									cancels={rereservedCancels}
									editingRereservationRowMap={editingRereservationRowMap}
									updateEditingRereservationRow={updateEditingRereservationRow}
									onClickUpdateButton={onClickUpdateButtonForRereservation}
									onClickActionButton={noopActionButton}
									onClickRereservationRowEdit={onClickRereservationRowEdit}
									cancelRereservationRowEdit={cancelRereservationRowEdit}
								/>
							</Tabs.Content>
						</div>
					) : (
						<div>
							<Tabs.Content value="INVOICE_NOT_SENT">
								<CancelsTableView
									currentTab={currentTab}
									cancels={invoiceNotSentCancels}
									enableLapsedHighlight={enableLapsedHighlight}
									editingRowMap={editingRowMap}
									checkedCancelMap={checkedNotSentInvoiceCancelMap}
									availableCouponMasters={couponMasters ?? []}
									selectableCancelTypes={selectableCancelTypes}
									enableDefaultCouponMasterForNoConfirmationCancel={
										enableDefaultCouponMasterForNoConfirmationCancel
									}
									defaultCouponMaster={defaultCouponMaster}
									updateRow={updateRow}
									onEditCancelType={onEditCancelType}
									onEditOriginPrice={onEditOriginPrice}
									onClickUpdateButton={onClickUpdateButton}
									onClickActionButton={onClickSendInvoice}
									cancelRowEdit={cancelRowEdit}
									onClickRowEdit={onClickRowEdit}
									onClickCheckbox={(cancel) =>
										onClickCheckbox(cancel, "INVOICE_NOT_SENT")
									}
									onSelectAllCheckbox={onSelectAllCheckbox}
									sortableKeys={sortableKeys}
									sort={sort}
									onClickSortKey={toggleSort}
								/>
							</Tabs.Content>
							<Tabs.Content value="INVOICE_SENT">
								<CancelsTableView
									currentTab={currentTab}
									cancels={invoiceSentCancels}
									enableLapsedHighlight={false}
									editingRowMap={editingRowMap}
									availableCouponMasters={couponMasters ?? []}
									selectableCancelTypes={selectableCancelTypes}
									enableDefaultCouponMasterForNoConfirmationCancel={
										enableDefaultCouponMasterForNoConfirmationCancel
									}
									defaultCouponMaster={defaultCouponMaster}
									updateRow={updateRow}
									onEditCancelType={onEditCancelType}
									onEditOriginPrice={onEditOriginPrice}
									onClickUpdateButton={onClickUpdateButton}
									onClickActionButton={onClickAbortClaim}
									cancelRowEdit={cancelRowEdit}
									onClickRowEdit={onClickRowEdit}
									sortableKeys={sortableKeys}
									sort={sort}
									onClickSortKey={toggleSort}
								/>
							</Tabs.Content>
							<Tabs.Content value="PAID">
								<CancelsTableView
									currentTab={currentTab}
									cancels={paidCancels}
									enableLapsedHighlight={false}
									editingRowMap={editingRowMap}
									availableCouponMasters={couponMasters ?? []}
									selectableCancelTypes={selectableCancelTypes}
									enableDefaultCouponMasterForNoConfirmationCancel={
										enableDefaultCouponMasterForNoConfirmationCancel
									}
									defaultCouponMaster={defaultCouponMaster}
									updateRow={updateRow}
									onEditCancelType={onEditCancelType}
									onEditOriginPrice={onEditOriginPrice}
									onClickUpdateButton={onClickUpdateButton}
									onClickActionButton={onClickSendInvoice}
									cancelRowEdit={cancelRowEdit}
									onClickRowEdit={onClickRowEdit}
									sortableKeys={sortableKeys}
									sort={sort}
									onClickSortKey={toggleSort}
								/>
							</Tabs.Content>
							<Tabs.Content value="NOT_SUBJECT">
								<CancelsTableView
									currentTab={currentTab}
									cancels={notSubjectCancels}
									enableLapsedHighlight={false}
									editingRowMap={editingRowMap}
									checkedCancelMap={checkedNotSubjectCancelIdMap}
									availableCouponMasters={couponMasters ?? []}
									selectableCancelTypes={selectableCancelTypes}
									enableDefaultCouponMasterForNoConfirmationCancel={
										enableDefaultCouponMasterForNoConfirmationCancel
									}
									defaultCouponMaster={defaultCouponMaster}
									updateRow={updateRow}
									onEditCancelType={onEditCancelType}
									onEditOriginPrice={onEditOriginPrice}
									onClickUpdateButton={onClickUpdateButton}
									onClickActionButton={onClickSendInvoice}
									cancelRowEdit={cancelRowEdit}
									onClickRowEdit={onClickRowEdit}
									onClickCheckbox={(cancel) =>
										onClickCheckbox(cancel, "NOT_SUBJECT")
									}
									onSelectAllCheckbox={onSelectAllCheckbox}
									sortableKeys={sortableKeys}
									sort={sort}
									onClickSortKey={toggleSort}
								/>
							</Tabs.Content>
							<Tabs.Content value="ENCOURAGING_RERESERVATION_NOT_SCHEDULED">
								<CancelsForRereservationTableView
									currentTab="ENCOURAGING_RERESERVATION_NOT_SCHEDULED"
									cancels={encourageRereservationMessageNotSentCancels}
									editingRereservationRowMap={editingRereservationRowMap}
									updateEditingRereservationRow={updateEditingRereservationRow}
									onClickUpdateButton={onClickUpdateButtonForRereservation}
									onClickActionButton={onClickScheduleEncourageRereservation}
									onClickRereservationRowEdit={onClickRereservationRowEdit}
									cancelRereservationRowEdit={cancelRereservationRowEdit}
								/>
							</Tabs.Content>
							<Tabs.Content value="ENCOURAGING_RERESERVATION_SCHEDULED">
								<CancelsForRereservationTableView
									currentTab="ENCOURAGING_RERESERVATION_SCHEDULED"
									cancels={encourageRereservationMessageSentCancels}
									editingRereservationRowMap={editingRereservationRowMap}
									updateEditingRereservationRow={updateEditingRereservationRow}
									onClickUpdateButton={onClickUpdateButtonForRereservation}
									onClickActionButton={onClickScheduleEncourageRereservation}
									onClickRereservationRowEdit={onClickRereservationRowEdit}
									cancelRereservationRowEdit={cancelRereservationRowEdit}
								/>
							</Tabs.Content>
							<Tabs.Content value="RERESERVED">
								<CancelsForRereservationTableView
									currentTab="RERESERVED"
									cancels={rereservedCancels}
									editingRereservationRowMap={editingRereservationRowMap}
									updateEditingRereservationRow={updateEditingRereservationRow}
									onClickUpdateButton={onClickUpdateButtonForRereservation}
									onClickActionButton={noopActionButton}
									onClickRereservationRowEdit={onClickRereservationRowEdit}
									cancelRereservationRowEdit={cancelRereservationRowEdit}
								/>
							</Tabs.Content>
						</div>
					)}
				</Tabs.Root>
			</div>
		</div>
	)
}

export default HomePage
